import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { filter, map } from 'rxjs';
import { UserService } from '@aeCore/services/user.service';
import { userInitialsFromUser } from '@aeCore/utils/user-avatar.util';

interface Breadcrumb { label: string; route?: string; }

@Component({
  selector: 'app-topbar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule],
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.scss',
})
export class TopbarComponent {
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);

  readonly title = signal('Dashboard');
  readonly breadcrumbs = signal<Breadcrumb[]>([
    { label: 'Overview' },
    { label: 'Dashboard' },
  ]);
  readonly searchQuery = signal('');
  readonly userInitials = toSignal(
    this.userService.currentUser$.pipe(
      map((user) => (user ? userInitialsFromUser(user) : '?')),
    ),
    { initialValue: '?' },
  );

  constructor() {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((event) => this.updateFromUrl((event as NavigationEnd).urlAfterRedirects));
    this.updateFromUrl(this.router.url);
  }

  private updateFromUrl(url: string): void {
    const segments = url.split('?')[0].split('/').filter(Boolean);
    if (!segments.length) {
      this.title.set('Dashboard');
      this.breadcrumbs.set([{ label: 'Overview' }, { label: 'Dashboard' }]);
      return;
    }
    const [head, ...rest] = segments;
    if (head === 'country-lane-management') {
      this.title.set('Country Lane Management');
      const crumbs: Breadcrumb[] = [{ label: this.sectionFor(head) }, { label: 'Country Lane Management' }];
      rest.forEach((s) => crumbs.push({ label: this.pretty(s) }));
      this.breadcrumbs.set(crumbs);
      return;
    }
    if (head === 'fx-config') {
      this.title.set('FX Configuration');
      this.breadcrumbs.set([{ label: this.sectionFor(head) }, { label: 'FX Configuration' }]);
      return;
    }
    if (head === 'partners') {
      this.title.set('Partner Management');
      this.breadcrumbs.set([
        { label: 'Configuration' },
        { label: 'Partner Management' },
      ]);
      return;
    }
    if (head === 'transactions') {
      this.title.set('');
      this.breadcrumbs.set([]);
      return;
    }
    const label = this.pretty(head);
    this.title.set(label);
    const crumbs: Breadcrumb[] = [{ label: this.sectionFor(head) }, { label }];
    rest.forEach((s) => crumbs.push({ label: this.pretty(s) }));
    this.breadcrumbs.set(crumbs);
  }

  private pretty(slug: string): string {
    return slug
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (m) => m.toUpperCase());
  }

  private sectionFor(slug: string): string {
    const sections: Record<string, string> = {
      dashboard: 'Overview',
      users: 'User Ops',
      'partner-kyc': 'User Ops',
      transactions: 'User Ops',
      'fee-pricing': 'Configuration',
      'country-lane-management': 'Configuration',
      'fx-config': 'Configuration',
      partners: 'Configuration',
      'aml-compliance': 'Compliance',
      'admin-users': 'System',
      settings: 'System',
      'partner-onboarding': 'Configuration',
    };
    return sections[slug] ?? 'Admin';
  }
}
